import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { useTheme, type Theme } from '@mui/material/styles'

import type { ManageEvent } from '../manage-events.slice'

// The cards reconcile with the tabs under any filters: EVENTS is the two tab counts summed,
// and OPEN + CLOSED is the Scheduled count.
type Props = {
  scheduledEvents: ManageEvent[]
  unscheduledCount: number
  loading: boolean
}

type CardDef = {
  label: string
  count: (scheduled: ManageEvent[], unscheduledCount: number) => number
  color: (theme: Theme) => string
}

let cards: CardDef[] = [
  {
    label: 'EVENTS',
    count: (s, unscheduledCount) => s.length + unscheduledCount,
    color: (t) => t.palette.analyticsColors.events,
  },
  {
    label: 'REGISTRATION OPEN',
    count: (e) =>
      e.filter((ev) => ev.registrationStatus === 'registration-open').length,
    color: (t) => t.palette.success.main,
  },
  {
    label: 'REGISTRATION CLOSED',
    count: (e) =>
      e.filter((ev) => ev.registrationStatus === 'registration-closed').length,
    color: (t) => t.palette.info.main,
  },
]

let KpiCard = ({
  label,
  number,
  numberColor,
}: {
  label: string
  number: number | null
  numberColor: string
}) => (
  <Paper className="flex flex-col flex-1 p-5 gap-1" style={{ minWidth: 140 }}>
    {number === null ? (
      <Skeleton variant="text" width={48} height={40} />
    ) : (
      <Typography
        variant="h4"
        component="span"
        sx={{ color: numberColor, fontWeight: 500, lineHeight: 1 }}
      >
        {number}
      </Typography>
    )}
    <Typography
      variant="caption"
      sx={{ color: 'text.secondary', letterSpacing: '0.08em' }}
    >
      {label}
    </Typography>
  </Paper>
)

let EventsKpiCards = ({
  scheduledEvents,
  unscheduledCount,
  loading,
}: Props) => {
  let theme = useTheme()

  return (
    <Box role="status" aria-live="polite" className="flex flex-wrap gap-4">
      {loading ? <span className="sr-only">Loading events data</span> : null}
      {cards.map((card) => (
        <KpiCard
          key={card.label}
          label={card.label}
          number={
            loading ? null : card.count(scheduledEvents, unscheduledCount)
          }
          numberColor={card.color(theme)}
        />
      ))}
    </Box>
  )
}

export default EventsKpiCards
